import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge, StatusBadge } from '../components/ui/Badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useAuth } from '../context/AuthContext';
import { partnerService } from '../services/partnerService';
import { PartnerStaffUser } from '../types/partner';
import {
  User,
  Users,
  Shield,
  Search,
  Plus,
  AlertCircle,
  CheckCircle,
  Plug,
  MoreHorizontal,
  Pencil,
  Trash2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { cn } from '../lib/utils';
import IntegrationsView from './IntegrationsView';

// ─── Zod Schemas ────────────────────────────────────────────────────

const profileSchema = z.object({
  partnerName: z.string().min(1, 'Partner name is required'),
  adminFirstName: z.string().min(1, 'Admin first name is required'),
  adminLastName: z.string().min(1, 'Admin last name is required'),
  adminEmail: z.string().min(1, 'Admin email is required').email('Invalid email address'),
  adminContactNumber: z.string().optional(),
  country: z.string().optional(),
  addressLine1: z.string().optional(),
  addressLine2: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  postalCode: z.string().optional(),
  website: z.string().optional(),
  partnerEmail: z.string().optional().refine(
    (val) => !val || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val),
    { message: 'Invalid email address' }
  ),
  partnerContactNumber: z.string().optional(),
});

type ProfileFormData = z.infer<typeof profileSchema>;

const addUserSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email address'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  isAdmin: z.boolean(),
});

type AddUserFormData = z.infer<typeof addUserSchema>;

// ─── Utility ────────────────────────────────────────────────────────

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

// ─── Main Component ─────────────────────────────────────────────────

export function SettingsView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    partner,
    managedCustomers,
    isAlsoCustomer,
    hasPartnerAccess,
    isPartnerAdmin,
    currentUserId,
    error: authError,
  } = useAuth();

  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<'profile' | 'users' | 'permissions' | 'integrations'>(
    (tabFromUrl as 'profile' | 'users' | 'permissions' | 'integrations') || 'profile'
  );

  const handleTabChange = (tab: typeof activeTab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const [users, setUsers] = useState<PartnerStaffUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<PartnerStaffUser>>({});
  const [isAddingUser, setIsAddingUser] = useState(false);

  // Pagination state for Users tab
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);

  // Partner profile editing state
  const [isEditing, setIsEditing] = useState(false);

  // Profile form with react-hook-form + zod
  const profileForm = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      partnerName: '',
      adminFirstName: '',
      adminLastName: '',
      adminEmail: '',
      adminContactNumber: '',
      country: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      postalCode: '',
      website: '',
      partnerEmail: '',
      partnerContactNumber: '',
    },
  });

  // Add user form with react-hook-form + zod
  const addUserForm = useForm<AddUserFormData>({
    resolver: zodResolver(addUserSchema),
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      isAdmin: false,
    },
  });

  // Auto-dismiss success messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Load partner staff
  const loadUsers = useCallback(async () => {
    if (!partner?.partnerId) return;

    setIsLoading(true);
    setError(null);
    try {
      const response = await partnerService.getPartnerStaff(partner.partnerId);
      if (response.success && response.data) {
        setUsers(response.data);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load staff');
    } finally {
      setIsLoading(false);
    }
  }, [partner?.partnerId]);

  useEffect(() => {
    if (activeTab === 'users') {
      loadUsers();
    }
  }, [activeTab, partner?.partnerId, loadUsers]);

  // Initialize editing form with partner data
  useEffect(() => {
    if (partner) {
      profileForm.reset({
        partnerName: partner.partnerName || '',
        adminFirstName: partner.adminFirstName || '',
        adminLastName: partner.adminLastName || '',
        adminEmail: partner.adminEmail || '',
        adminContactNumber: partner.adminContactNumber || '',
        country: partner.country || '',
        addressLine1: partner.addressLine1 || '',
        addressLine2: partner.addressLine2 || '',
        city: partner.city || '',
        state: partner.state || '',
        postalCode: partner.postalCode || '',
        website: partner.website || '',
        partnerEmail: partner.partnerEmail || '',
        partnerContactNumber: partner.partnerContactNumber || '',
      });
    }
  }, [partner, profileForm]);

  // Filter users based on search
  const filteredUsers = users.filter((user) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      user.firstName?.toLowerCase().includes(search) ||
      user.lastName?.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search)
    );
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredUsers.length / entriesPerPage);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage
  );

  // Reset page when search/entries change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, entriesPerPage]);

  const handleEditUser = (userId: string) => {
    const user = users.find((u) => u.partnerUserId === userId);
    if (user) {
      setEditingUserId(userId);
      setEditForm({
        firstName: user.firstName,
        lastName: user.lastName,
        isAdmin: user.isAdmin,
      });
    }
  };

  const handleAddUser = async (data: AddUserFormData) => {
    if (!partner?.partnerId) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await partnerService.addPartnerStaff(partner.partnerId, {
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        isAdmin: data.isAdmin,
      });

      if (response.success) {
        setSuccess('Staff member added successfully');
        setIsAddingUser(false);
        addUserForm.reset();
        await loadUsers();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to add staff member');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveUser = async (userId: string) => {
    if (!partner?.partnerId) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await partnerService.updatePartnerStaff(partner.partnerId, userId, {
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        isAdmin: editForm.isAdmin,
      });

      if (response.success) {
        setSuccess('Staff member updated successfully');
        setEditingUserId(null);
        setEditForm({});
        await loadUsers();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update staff member');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveUser = async (userId: string) => {
    if (!partner?.partnerId) return;
    if (!window.confirm('Are you sure you want to remove this staff member?')) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await partnerService.removePartnerStaff(partner.partnerId, userId);

      if (response.success) {
        setSuccess('Staff member removed successfully');
        setEditingUserId(null);
        setEditForm({});
        await loadUsers();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to remove staff member');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveProfile = async (data: ProfileFormData) => {
    if (!partner?.partnerId) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await partnerService.updatePartnerProfile(partner.partnerId, data);

      if (response.success) {
        setSuccess('Partner profile updated successfully');
        setIsEditing(false);
        window.location.reload();
      } else {
        throw new Error(response.error || 'Failed to update profile');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  // Check if user has partner access
  if (!hasPartnerAccess) {
    return (
      <div className="flex h-full bg-slate-50 p-6 justify-center items-center">
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 shadow-sm max-w-md">
          <div className="flex items-start gap-4">
            <div className="p-2 rounded-lg bg-amber-100">
              <AlertCircle className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-amber-800">Partner Access Required</h3>
              <p className="text-sm text-amber-700 mt-1">
                You are not registered as a partner in our system. Please contact your administrator
                to set up partner access.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'profile' as const, name: 'Partner Profile', icon: User },
    ...(isPartnerAdmin ? [{ id: 'users' as const, name: 'Partner Users', icon: Users }] : []),
    { id: 'permissions' as const, name: 'Permissions', icon: Shield },
    { id: 'integrations' as const, name: 'Integrations', icon: Plug },
  ];

  return (
    <div className="flex h-full bg-slate-50">
      <div className="flex w-full">
        {/* Secondary Sidebar - Duralux style */}
        <div className="w-[260px] flex-shrink-0 bg-white border-r border-slate-200">
          <div className="sticky top-0">
            {/* Header */}
            <div className="h-14 px-6 flex items-center border-b border-slate-200">
              <h4 className="text-base font-bold text-slate-900">Settings</h4>
            </div>

            {/* Navigation */}
            <div className="py-3 px-3">
              <nav className="space-y-1">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => handleTabChange(tab.id)}
                      className={cn(
                        'w-full flex items-center gap-3 px-3 py-2.5 rounded-md transition-all text-[13px] font-semibold',
                        isActive
                          ? 'bg-orange-50 text-orange-700'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                      )}
                    >
                      <Icon
                        className={cn(
                          'h-4 w-4 shrink-0',
                          isActive ? 'text-orange-600' : 'text-slate-400'
                        )}
                      />
                      {tab.name}
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6 max-w-[1200px]">
            {/* Error and Success Messages */}
            {(error || authError) && (
              <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-3">
                  <AlertCircle className="h-5 w-5 text-rose-600 shrink-0" />
                  <span className="text-sm text-rose-800 flex-1">{error || authError}</span>
                  <button
                    onClick={() => setError(null)}
                    className="text-rose-600 hover:text-rose-800 transition-colors text-xl leading-none"
                  >
                    &times;
                  </button>
                </div>
              </div>
            )}

            {success && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-emerald-600 shrink-0" />
                  <span className="text-sm text-emerald-800 flex-1">{success}</span>
                  <button
                    onClick={() => setSuccess(null)}
                    className="text-emerald-600 hover:text-emerald-800 transition-colors text-xl leading-none"
                  >
                    &times;
                  </button>
                </div>
              </div>
            )}

            {/* ─── Profile Tab ─────────────────────────────────────────── */}
            {activeTab === 'profile' && (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200">
                  <h3 className="font-semibold text-slate-900">Partner Profile</h3>
                  <p className="text-sm text-slate-500 mt-1">
                    Manage your partner organization details
                  </p>
                </div>
                <div className="p-6">
                  {partner ? (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-slate-900">
                              Company Information
                            </h3>
                            <Button
                              size="sm"
                              variant={isEditing ? 'outline' : 'default'}
                              onClick={() => {
                                if (isEditing) {
                                  setIsEditing(false);
                                  profileForm.reset();
                                } else {
                                  setIsEditing(true);
                                }
                              }}
                            >
                              {isEditing ? 'Cancel' : 'Edit Profile'}
                            </Button>
                          </div>

                          {isEditing ? (
                            <form
                              onSubmit={profileForm.handleSubmit(handleSaveProfile)}
                              className="space-y-4"
                            >
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="md:col-span-2 space-y-1.5">
                                  <Label htmlFor="partnerName">
                                    Partner Name <span className="text-rose-500">*</span>
                                  </Label>
                                  <Input
                                    id="partnerName"
                                    {...profileForm.register('partnerName')}
                                    className={cn(
                                      profileForm.formState.errors.partnerName &&
                                        'border-rose-300 focus-visible:ring-rose-500'
                                    )}
                                  />
                                  {profileForm.formState.errors.partnerName && (
                                    <p className="text-xs text-rose-600">
                                      {profileForm.formState.errors.partnerName.message}
                                    </p>
                                  )}
                                </div>
                              </div>

                              <div className="border-t border-slate-200 pt-4">
                                <h4 className="text-md font-semibold text-slate-900 mb-3">
                                  Address
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="md:col-span-2 space-y-1.5">
                                    <Label htmlFor="addressLine1">Street Address</Label>
                                    <Input
                                      id="addressLine1"
                                      {...profileForm.register('addressLine1')}
                                      placeholder="123 Main Street"
                                    />
                                  </div>
                                  <div className="md:col-span-2 space-y-1.5">
                                    <Label htmlFor="addressLine2">Address Line 2</Label>
                                    <Input
                                      id="addressLine2"
                                      {...profileForm.register('addressLine2')}
                                      placeholder="Suite 100, Level 5"
                                    />
                                  </div>
                                  <div className="space-y-1.5">
                                    <Label htmlFor="city">City</Label>
                                    <Input
                                      id="city"
                                      {...profileForm.register('city')}
                                      placeholder="Sydney"
                                    />
                                  </div>
                                  <div className="space-y-1.5">
                                    <Label htmlFor="state">State / Province</Label>
                                    <Input
                                      id="state"
                                      {...profileForm.register('state')}
                                      placeholder="NSW"
                                    />
                                  </div>
                                  <div className="space-y-1.5">
                                    <Label htmlFor="postalCode">Postal Code</Label>
                                    <Input
                                      id="postalCode"
                                      {...profileForm.register('postalCode')}
                                      placeholder="2000"
                                    />
                                  </div>
                                  <div className="space-y-1.5">
                                    <Label htmlFor="country">Country</Label>
                                    <Input
                                      id="country"
                                      {...profileForm.register('country')}
                                      placeholder="Australia"
                                    />
                                  </div>
                                </div>
                              </div>

                              <div className="border-t border-slate-200 pt-4">
                                <h4 className="text-md font-semibold text-slate-900 mb-3">
                                  Administrator Information
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="space-y-1.5">
                                    <Label htmlFor="adminFirstName">
                                      Admin First Name <span className="text-rose-500">*</span>
                                    </Label>
                                    <Input
                                      id="adminFirstName"
                                      {...profileForm.register('adminFirstName')}
                                      className={cn(
                                        profileForm.formState.errors.adminFirstName &&
                                          'border-rose-300 focus-visible:ring-rose-500'
                                      )}
                                    />
                                    {profileForm.formState.errors.adminFirstName && (
                                      <p className="text-xs text-rose-600">
                                        {profileForm.formState.errors.adminFirstName.message}
                                      </p>
                                    )}
                                  </div>
                                  <div className="space-y-1.5">
                                    <Label htmlFor="adminLastName">
                                      Admin Last Name <span className="text-rose-500">*</span>
                                    </Label>
                                    <Input
                                      id="adminLastName"
                                      {...profileForm.register('adminLastName')}
                                      className={cn(
                                        profileForm.formState.errors.adminLastName &&
                                          'border-rose-300 focus-visible:ring-rose-500'
                                      )}
                                    />
                                    {profileForm.formState.errors.adminLastName && (
                                      <p className="text-xs text-rose-600">
                                        {profileForm.formState.errors.adminLastName.message}
                                      </p>
                                    )}
                                  </div>
                                  <div className="space-y-1.5">
                                    <Label htmlFor="adminEmail">
                                      Admin Email <span className="text-rose-500">*</span>
                                    </Label>
                                    <Input
                                      id="adminEmail"
                                      type="email"
                                      {...profileForm.register('adminEmail')}
                                      className={cn(
                                        profileForm.formState.errors.adminEmail &&
                                          'border-rose-300 focus-visible:ring-rose-500'
                                      )}
                                    />
                                    {profileForm.formState.errors.adminEmail && (
                                      <p className="text-xs text-rose-600">
                                        {profileForm.formState.errors.adminEmail.message}
                                      </p>
                                    )}
                                  </div>
                                  <div className="space-y-1.5">
                                    <Label htmlFor="adminContactNumber">Admin Contact Number</Label>
                                    <Input
                                      id="adminContactNumber"
                                      type="tel"
                                      {...profileForm.register('adminContactNumber')}
                                    />
                                  </div>
                                </div>
                              </div>

                              <div className="border-t border-slate-200 pt-4">
                                <h4 className="text-md font-semibold text-slate-900 mb-3">
                                  Company Contact Information
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="space-y-1.5">
                                    <Label htmlFor="website">Website</Label>
                                    <Input
                                      id="website"
                                      type="url"
                                      {...profileForm.register('website')}
                                    />
                                  </div>
                                  <div className="space-y-1.5">
                                    <Label htmlFor="partnerEmail">Partner Email</Label>
                                    <Input
                                      id="partnerEmail"
                                      type="email"
                                      {...profileForm.register('partnerEmail')}
                                      className={cn(
                                        profileForm.formState.errors.partnerEmail &&
                                          'border-rose-300 focus-visible:ring-rose-500'
                                      )}
                                    />
                                    {profileForm.formState.errors.partnerEmail && (
                                      <p className="text-xs text-rose-600">
                                        {profileForm.formState.errors.partnerEmail.message}
                                      </p>
                                    )}
                                  </div>
                                  <div className="space-y-1.5">
                                    <Label htmlFor="partnerContactNumber">
                                      Partner Contact Number
                                    </Label>
                                    <Input
                                      id="partnerContactNumber"
                                      type="tel"
                                      {...profileForm.register('partnerContactNumber')}
                                    />
                                  </div>
                                </div>
                              </div>

                              <div className="flex gap-2 pt-4 border-t border-slate-200">
                                <Button type="submit" loading={isLoading}>
                                  Save Changes
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() => {
                                    setIsEditing(false);
                                    profileForm.reset();
                                  }}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </form>
                          ) : (
                            <div className="space-y-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="p-3 bg-slate-50 rounded-lg">
                                  <label className="text-xs text-slate-500">Partner ID</label>
                                  <p className="font-semibold text-sm font-mono">
                                    {partner.partnerId}
                                  </p>
                                </div>
                                <div className="p-3 bg-slate-50 rounded-lg">
                                  <label className="text-xs text-slate-500">Partner Name</label>
                                  <p className="font-semibold">{partner.partnerName}</p>
                                </div>
                              </div>

                              <div className="border-t border-slate-200 pt-4">
                                <h4 className="text-md font-semibold text-slate-900 mb-3">
                                  Address
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="p-3 bg-slate-50 rounded-lg md:col-span-2">
                                    <label className="text-xs text-slate-500">Street Address</label>
                                    <p className="font-semibold">
                                      {partner.addressLine1 || 'Not specified'}
                                    </p>
                                  </div>
                                  {partner.addressLine2 && (
                                    <div className="p-3 bg-slate-50 rounded-lg md:col-span-2">
                                      <label className="text-xs text-slate-500">
                                        Address Line 2
                                      </label>
                                      <p className="font-semibold">{partner.addressLine2}</p>
                                    </div>
                                  )}
                                  <div className="p-3 bg-slate-50 rounded-lg">
                                    <label className="text-xs text-slate-500">City</label>
                                    <p className="font-semibold">
                                      {partner.city || 'Not specified'}
                                    </p>
                                  </div>
                                  <div className="p-3 bg-slate-50 rounded-lg">
                                    <label className="text-xs text-slate-500">
                                      State / Province
                                    </label>
                                    <p className="font-semibold">
                                      {partner.state || 'Not specified'}
                                    </p>
                                  </div>
                                  <div className="p-3 bg-slate-50 rounded-lg">
                                    <label className="text-xs text-slate-500">Postal Code</label>
                                    <p className="font-semibold">
                                      {partner.postalCode || 'Not specified'}
                                    </p>
                                  </div>
                                  <div className="p-3 bg-slate-50 rounded-lg">
                                    <label className="text-xs text-slate-500">Country</label>
                                    <p className="font-semibold">
                                      {partner.country || 'Not specified'}
                                    </p>
                                  </div>
                                </div>
                              </div>

                              <div className="border-t border-slate-200 pt-4">
                                <h4 className="text-md font-semibold text-slate-900 mb-3">
                                  Administrator Information
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="p-3 bg-slate-50 rounded-lg">
                                    <label className="text-xs text-slate-500">Admin Name</label>
                                    <p className="font-semibold">
                                      {partner.adminFirstName} {partner.adminLastName}
                                    </p>
                                  </div>
                                  <div className="p-3 bg-slate-50 rounded-lg">
                                    <label className="text-xs text-slate-500">Admin Email</label>
                                    <p className="font-semibold">{partner.adminEmail}</p>
                                  </div>
                                  <div className="p-3 bg-slate-50 rounded-lg">
                                    <label className="text-xs text-slate-500">
                                      Admin Contact Number
                                    </label>
                                    <p className="font-semibold">
                                      {partner.adminContactNumber || 'Not provided'}
                                    </p>
                                  </div>
                                </div>
                              </div>

                              <div className="border-t border-slate-200 pt-4">
                                <h4 className="text-md font-semibold text-slate-900 mb-3">
                                  Company Contact Information
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="p-3 bg-slate-50 rounded-lg">
                                    <label className="text-xs text-slate-500">Website</label>
                                    <p className="font-semibold">
                                      {partner.website || 'Not provided'}
                                    </p>
                                  </div>
                                  <div className="p-3 bg-slate-50 rounded-lg">
                                    <label className="text-xs text-slate-500">Partner Email</label>
                                    <p className="font-semibold">
                                      {partner.partnerEmail || 'Not provided'}
                                    </p>
                                  </div>
                                  <div className="p-3 bg-slate-50 rounded-lg">
                                    <label className="text-xs text-slate-500">
                                      Partner Contact Number
                                    </label>
                                    <p className="font-semibold">
                                      {partner.partnerContactNumber || 'Not provided'}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="space-y-4">
                          <h3 className="text-lg font-semibold text-slate-900">Account Details</h3>
                          <div className="space-y-3">
                            <div className="p-4 bg-slate-50 rounded-lg flex items-center justify-between">
                              <div>
                                <label className="text-xs text-slate-500">Status</label>
                                <div className="mt-1">
                                  <StatusBadge
                                    status={
                                      partner.status === 'Active' ? 'active' : 'inactive'
                                    }
                                    label={partner.status}
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="p-4 bg-slate-50 rounded-lg">
                              <label className="text-xs text-slate-500">Account Created</label>
                              <p className="font-semibold mt-1">{formatDate(partner.createdAt)}</p>
                            </div>
                            <div className="p-4 bg-slate-50 rounded-lg">
                              <label className="text-xs text-slate-500">Managed Customers</label>
                              <p className="font-semibold mt-1">{managedCustomers.length}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="w-12 h-12 rounded-full border-4 border-slate-200 border-t-orange-600 animate-spin mx-auto" />
                      <p className="text-sm text-slate-500 mt-4">Loading partner profile...</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ─── Users Tab ───────────────────────────────────────────── */}
            {activeTab === 'users' && (
              <div className="space-y-4">
                {!isPartnerAdmin ? (
                  <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b border-slate-200">
                      <h3 className="font-semibold text-slate-900">Partner Staff</h3>
                    </div>
                    <div className="p-6">
                      <div className="text-center py-12">
                        <Users className="mx-auto h-12 w-12 text-slate-300" />
                        <h3 className="mt-4 text-sm font-medium text-slate-900">
                          Administrator Access Required
                        </h3>
                        <p className="mt-2 text-sm text-slate-500">
                          Only partner administrators can view and manage staff members.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Header with Search and Add Button */}
                    <div className="flex items-center justify-between">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          className="w-64 pl-10"
                          placeholder="Search staff..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={loadUsers}
                          loading={isLoading}
                        >
                          Refresh
                        </Button>
                        <Button size="sm" onClick={() => setIsAddingUser(true)}>
                          <Plus className="h-4 w-4 mr-2" />
                          Add Staff
                        </Button>
                      </div>
                    </div>

                    {/* Add User Form */}
                    {isAddingUser && (
                      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-200">
                          <h3 className="font-semibold text-slate-900">Add New Staff Member</h3>
                        </div>
                        <div className="p-6">
                          <form
                            onSubmit={addUserForm.handleSubmit(handleAddUser)}
                            className="space-y-4"
                          >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="space-y-1.5">
                                <Label htmlFor="newEmail">
                                  Email <span className="text-rose-500">*</span>
                                </Label>
                                <Input
                                  id="newEmail"
                                  type="email"
                                  placeholder="user@example.com"
                                  {...addUserForm.register('email')}
                                  className={cn(
                                    addUserForm.formState.errors.email &&
                                      'border-rose-300 focus-visible:ring-rose-500'
                                  )}
                                />
                                {addUserForm.formState.errors.email && (
                                  <p className="text-xs text-rose-600">
                                    {addUserForm.formState.errors.email.message}
                                  </p>
                                )}
                              </div>
                              <div className="space-y-1.5">
                                <Label htmlFor="newFirstName">
                                  First Name <span className="text-rose-500">*</span>
                                </Label>
                                <Input
                                  id="newFirstName"
                                  placeholder="John"
                                  {...addUserForm.register('firstName')}
                                  className={cn(
                                    addUserForm.formState.errors.firstName &&
                                      'border-rose-300 focus-visible:ring-rose-500'
                                  )}
                                />
                                {addUserForm.formState.errors.firstName && (
                                  <p className="text-xs text-rose-600">
                                    {addUserForm.formState.errors.firstName.message}
                                  </p>
                                )}
                              </div>
                              <div className="space-y-1.5">
                                <Label htmlFor="newLastName">
                                  Last Name <span className="text-rose-500">*</span>
                                </Label>
                                <Input
                                  id="newLastName"
                                  placeholder="Doe"
                                  {...addUserForm.register('lastName')}
                                  className={cn(
                                    addUserForm.formState.errors.lastName &&
                                      'border-rose-300 focus-visible:ring-rose-500'
                                  )}
                                />
                                {addUserForm.formState.errors.lastName && (
                                  <p className="text-xs text-rose-600">
                                    {addUserForm.formState.errors.lastName.message}
                                  </p>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center gap-2">
                              <Checkbox
                                id="new-user-admin"
                                checked={addUserForm.watch('isAdmin')}
                                onCheckedChange={(checked) =>
                                  addUserForm.setValue('isAdmin', checked === true)
                                }
                              />
                              <Label htmlFor="new-user-admin" className="cursor-pointer">
                                Partner Administrator
                              </Label>
                            </div>

                            <div className="flex gap-2 pt-2">
                              <Button type="submit" loading={isLoading}>
                                Add Staff Member
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                  setIsAddingUser(false);
                                  addUserForm.reset();
                                }}
                              >
                                Cancel
                              </Button>
                            </div>
                          </form>
                        </div>
                      </div>
                    )}

                    {/* Users Table */}
                    {isLoading && !isAddingUser ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="w-8 h-8 rounded-full border-4 border-slate-200 border-t-orange-600 animate-spin" />
                        <span className="ml-3 text-slate-600">Loading staff...</span>
                      </div>
                    ) : filteredUsers.length === 0 ? (
                      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-200">
                          <h3 className="font-semibold text-slate-900">Partner Staff</h3>
                        </div>
                        <div className="p-6">
                          <div className="text-center py-12">
                            <Users className="mx-auto h-12 w-12 text-slate-300" />
                            <h3 className="mt-4 text-sm font-medium text-slate-900">
                              {searchTerm ? 'No Matching Staff' : 'No Staff Members'}
                            </h3>
                            <p className="mt-2 text-sm text-slate-500">
                              {searchTerm
                                ? 'Try adjusting your search terms'
                                : 'Add staff members to your partner organization'}
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                        {/* Show entries selector */}
                        {filteredUsers.length > 10 && (
                          <div className="px-6 py-3 border-b border-slate-100 flex items-center gap-2 text-sm text-slate-600">
                            <span>Show</span>
                            <select
                              value={entriesPerPage}
                              onChange={(e) => setEntriesPerPage(Number(e.target.value))}
                              className="border border-slate-200 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                            >
                              <option value={10}>10</option>
                              <option value={25}>25</option>
                              <option value={50}>50</option>
                            </select>
                            <span>entries</span>
                          </div>
                        )}

                        <div className="overflow-x-auto">
                          <table className="min-w-full">
                            <thead>
                              <tr className="border-b border-slate-200 bg-slate-50/50">
                                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                  Staff Member
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                  Email
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                  Role
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                  Status
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                  Actions
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {paginatedUsers.map((user) => (
                                <React.Fragment key={user.partnerUserId}>
                                  <tr
                                    className={cn(
                                      'transition-colors group',
                                      editingUserId === user.partnerUserId
                                        ? 'bg-orange-50/50'
                                        : 'hover:bg-slate-50/50'
                                    )}
                                  >
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="flex items-center">
                                        <div className="w-8 h-8 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-sm font-medium mr-3">
                                          {(
                                            user.firstName?.[0] || user.email[0]
                                          ).toUpperCase()}
                                        </div>
                                        <div className="text-sm font-medium text-slate-900">
                                          {user.firstName} {user.lastName}
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                                      {user.email}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <Badge variant={user.isAdmin ? 'success' : 'secondary'}>
                                        {user.isAdmin ? 'Admin' : 'Staff'}
                                      </Badge>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <StatusBadge
                                        status={
                                          user.status === 'Active' ? 'active' : 'inactive'
                                        }
                                        label={user.status}
                                      />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right">
                                      <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                          <button className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100">
                                            <MoreHorizontal className="h-4 w-4" />
                                          </button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="w-40">
                                          <DropdownMenuItem
                                            onClick={() =>
                                              handleEditUser(user.partnerUserId)
                                            }
                                            className="cursor-pointer"
                                          >
                                            <Pencil className="mr-2 h-4 w-4" />
                                            Edit
                                          </DropdownMenuItem>
                                          <DropdownMenuItem
                                            onClick={() =>
                                              handleRemoveUser(user.partnerUserId)
                                            }
                                            className="cursor-pointer text-rose-600 focus:text-rose-600"
                                          >
                                            <Trash2 className="mr-2 h-4 w-4" />
                                            Remove
                                          </DropdownMenuItem>
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                    </td>
                                  </tr>

                                  {/* Inline Edit Row */}
                                  {editingUserId === user.partnerUserId && (
                                    <tr>
                                      <td
                                        colSpan={5}
                                        className="bg-orange-50/30 border-t border-orange-100 p-0"
                                      >
                                        <div className="p-6">
                                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                            <div className="space-y-4">
                                              <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                                                <User className="w-4 h-4 text-orange-600" />
                                                Staff Details
                                              </h4>
                                              <div className="space-y-3">
                                                <div className="space-y-1.5">
                                                  <Label htmlFor={`edit-fn-${user.partnerUserId}`}>
                                                    First Name
                                                  </Label>
                                                  <Input
                                                    id={`edit-fn-${user.partnerUserId}`}
                                                    value={editForm.firstName || ''}
                                                    onChange={(e) =>
                                                      setEditForm({
                                                        ...editForm,
                                                        firstName: e.target.value,
                                                      })
                                                    }
                                                  />
                                                </div>
                                                <div className="space-y-1.5">
                                                  <Label htmlFor={`edit-ln-${user.partnerUserId}`}>
                                                    Last Name
                                                  </Label>
                                                  <Input
                                                    id={`edit-ln-${user.partnerUserId}`}
                                                    value={editForm.lastName || ''}
                                                    onChange={(e) =>
                                                      setEditForm({
                                                        ...editForm,
                                                        lastName: e.target.value,
                                                      })
                                                    }
                                                  />
                                                </div>
                                              </div>
                                            </div>

                                            <div className="space-y-4">
                                              <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                                                <Shield className="w-4 h-4 text-orange-600" />
                                                Role & Permissions
                                              </h4>
                                              <div className="p-3 bg-white rounded-lg border border-slate-200">
                                                <label
                                                  className={cn(
                                                    'flex items-center gap-3',
                                                    isPartnerAdmin &&
                                                      user.partnerUserId !== currentUserId
                                                      ? 'cursor-pointer'
                                                      : 'cursor-not-allowed opacity-60'
                                                  )}
                                                >
                                                  <Checkbox
                                                    checked={editForm.isAdmin || false}
                                                    onCheckedChange={(checked) =>
                                                      setEditForm({
                                                        ...editForm,
                                                        isAdmin: checked === true,
                                                      })
                                                    }
                                                    disabled={
                                                      !isPartnerAdmin ||
                                                      user.partnerUserId === currentUserId
                                                    }
                                                  />
                                                  <div className="flex-1">
                                                    <span className="text-sm font-medium text-slate-900">
                                                      Partner Administrator
                                                    </span>
                                                    <p className="text-xs text-slate-600 mt-0.5">
                                                      {user.partnerUserId === currentUserId
                                                        ? 'You cannot change your own admin status'
                                                        : 'Can manage customers and other partner staff'}
                                                    </p>
                                                  </div>
                                                </label>
                                              </div>
                                              <div className="bg-white rounded-lg border border-slate-200 p-4 space-y-3">
                                                <div className="flex justify-between">
                                                  <span className="text-xs text-slate-600">
                                                    User ID
                                                  </span>
                                                  <span className="text-xs font-mono text-slate-900">
                                                    {user.partnerUserId.slice(0, 8)}...
                                                  </span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-xs text-slate-600">
                                                    Email
                                                  </span>
                                                  <span className="text-xs font-medium text-slate-900">
                                                    {user.email}
                                                  </span>
                                                </div>
                                              </div>
                                            </div>
                                          </div>

                                          <div className="flex justify-between items-center mt-6 pt-4 border-t border-slate-200">
                                            <Button
                                              variant="ghost"
                                              onClick={() =>
                                                handleRemoveUser(user.partnerUserId)
                                              }
                                              className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                            >
                                              Remove Staff Member
                                            </Button>
                                            <div className="flex gap-3">
                                              <Button
                                                variant="ghost"
                                                onClick={() => {
                                                  setEditingUserId(null);
                                                  setEditForm({});
                                                }}
                                              >
                                                Cancel
                                              </Button>
                                              <Button
                                                onClick={() =>
                                                  handleSaveUser(user.partnerUserId)
                                                }
                                                loading={isLoading}
                                              >
                                                Save Changes
                                              </Button>
                                            </div>
                                          </div>
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                          <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-between text-sm">
                            <span className="text-slate-600">
                              Showing {(currentPage - 1) * entriesPerPage + 1} to{' '}
                              {Math.min(currentPage * entriesPerPage, filteredUsers.length)} of{' '}
                              {filteredUsers.length} entries
                            </span>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                              >
                                <ChevronLeft className="h-4 w-4" />
                              </button>
                              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                <button
                                  key={page}
                                  onClick={() => setCurrentPage(page)}
                                  className={cn(
                                    'w-8 h-8 rounded-md text-sm font-medium transition-colors',
                                    page === currentPage
                                      ? 'bg-orange-600 text-white'
                                      : 'text-slate-600 hover:bg-slate-100'
                                  )}
                                >
                                  {page}
                                </button>
                              ))}
                              <button
                                onClick={() =>
                                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                                }
                                disabled={currentPage === totalPages}
                                className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                              >
                                <ChevronRight className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* ─── Permissions Tab ─────────────────────────────────────── */}
            {activeTab === 'permissions' && (
              <div className="space-y-6">
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-slate-200">
                    <h3 className="font-semibold text-slate-900">Current Permissions</h3>
                    <p className="text-sm text-slate-500 mt-1">
                      View your current permissions and access levels
                    </p>
                  </div>
                  <div className="p-6">
                    <div className="space-y-3">
                      {partner?.permissions ? (
                        Object.entries(partner.permissions).map(([key, value]) => (
                          <div
                            key={key}
                            className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-100"
                          >
                            <div className="flex items-center gap-3">
                              <div
                                className={cn(
                                  'w-2 h-2 rounded-full',
                                  value ? 'bg-emerald-500' : 'bg-slate-300'
                                )}
                              />
                              <h4 className="font-medium text-slate-900 text-sm">
                                {key
                                  .replace(/([A-Z])/g, ' $1')
                                  .replace(/^./, (str) => str.toUpperCase())}
                              </h4>
                            </div>
                            <Badge variant={value ? 'success' : 'secondary'}>
                              {value ? 'Enabled' : 'Disabled'}
                            </Badge>
                          </div>
                        ))
                      ) : (
                        <p className="text-slate-500 text-sm">
                          No permissions information available.
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-slate-200">
                    <h3 className="font-semibold text-slate-900">Account Summary</h3>
                    <p className="text-sm text-slate-500 mt-1">
                      Overview of your partner account details
                    </p>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
                            <Users className="w-4 h-4 text-orange-600" />
                          </div>
                          <h4 className="font-medium text-slate-900 text-sm">
                            Customer Management
                          </h4>
                        </div>
                        <p className="text-sm text-slate-600 ml-11">
                          Managing {managedCustomers.length} customer
                          {managedCustomers.length !== 1 ? 's' : ''}
                        </p>
                      </div>

                      <div className="p-4 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
                            <Shield className="w-4 h-4 text-orange-600" />
                          </div>
                          <h4 className="font-medium text-slate-900 text-sm">Account Type</h4>
                        </div>
                        <p className="text-sm text-slate-600 ml-11">
                          {isAlsoCustomer
                            ? 'You also have customer account access'
                            : 'Partner-only account'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ─── Integrations Tab ────────────────────────────────────── */}
            {activeTab === 'integrations' && <IntegrationsView />}
          </div>
        </div>
      </div>
    </div>
  );
}
